import { AlertTriangle, Check, Info, ShieldAlert } from 'lucide-react';
import React, { useState } from 'react';

import { Pill } from '@/components/common/Pill';
import { cn } from '@/lib/utils';
import { useGetClaimFlagsQuery, useResolveFlagMutation } from '@/services/api/miniTortService';

import { getFlagSeverityVariant } from './stage-config';
import type { MiniTortClaimFlag } from './types';
import { FlagSeverity } from './types';
import { extractApiError } from './utils';

interface MiniTortFlagsProps {
  claimId: string;
  className?: string;
}

export const MiniTortFlags: React.FC<MiniTortFlagsProps> = ({ claimId, className }) => {
  const { data: flags, isLoading } = useGetClaimFlagsQuery(claimId);

  if (isLoading) {
    return (
      <div className={cn('rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900', className)}>
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Flags</h3>
        <p className="mt-2 text-sm text-zinc-500">Loading...</p>
      </div>
    );
  }

  const unresolvedFlags = flags?.filter((f) => !f.resolved) ?? [];
  const resolvedFlags = flags?.filter((f) => f.resolved) ?? [];

  const blockerCount = unresolvedFlags.filter((f) => f.severity === FlagSeverity.BLOCKER).length;
  const warningCount = unresolvedFlags.filter((f) => f.severity === FlagSeverity.WARNING).length;

  return (
    <div className={cn(
      'rounded-lg border bg-white p-4 dark:bg-zinc-900',
      blockerCount > 0
        ? 'border-red-300 ring-1 ring-red-200 dark:border-red-700 dark:ring-red-900/50'
        : 'border-zinc-200 dark:border-zinc-800',
      className,
    )}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
          Flags
          {blockerCount > 0 && (
            <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700 dark:bg-red-900/50 dark:text-red-300">
              {blockerCount} BLOCKING
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {blockerCount > 0 && (
            <span className="flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
              <ShieldAlert className="h-3.5 w-3.5" />
              {blockerCount} blocker{blockerCount !== 1 ? 's' : ''}
            </span>
          )}
          {warningCount > 0 && (
            <span className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-3.5 w-3.5" />
              {warningCount} warning{warningCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {unresolvedFlags.length === 0 && resolvedFlags.length === 0 && (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">No flags on this claim</p>
      )}

      {/* Unresolved flags */}
      {unresolvedFlags.length > 0 && (
        <div className="space-y-2">
          {unresolvedFlags.map((flag) => (
            <FlagCard key={flag.id} flag={flag} claimId={claimId} />
          ))}
        </div>
      )}

      {/* Resolved flags (collapsed) */}
      {resolvedFlags.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-medium text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300">
            {resolvedFlags.length} resolved flag{resolvedFlags.length !== 1 ? 's' : ''}
          </summary>
          <div className="mt-2 space-y-2">
            {resolvedFlags.map((flag) => (
              <FlagCard key={flag.id} flag={flag} claimId={claimId} resolved />
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

const FlagCard: React.FC<{
  flag: MiniTortClaimFlag;
  claimId: string;
  resolved?: boolean;
}> = ({ flag, claimId, resolved }) => {
  const [resolveFlag] = useResolveFlagMutation();
  const [isResolving, setIsResolving] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resolveMessage, setResolveMessage] = useState<string | null>(null);

  const getSeverityIcon = () => {
    if (flag.severity === FlagSeverity.BLOCKER) {
      return <ShieldAlert className="h-4 w-4 text-red-500" />;
    }
    if (flag.severity === FlagSeverity.WARNING) {
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    }
    return <Info className="h-4 w-4 text-zinc-400" />;
  };
  const severityIcon = getSeverityIcon();

  const handleResolve = async () => {
    if (!reason.trim()) {
      setError('Resolution reason is required');
      return;
    }
    try {
      const result = await resolveFlag({ claimId, flagId: flag.id, body: { resolved_reason: reason } }).unwrap();
      setIsResolving(false);
      setReason('');
      setError(null);
      // Backend returns { flag, advanced, advance_blocked_reason }
      const msg = (result as unknown as { advance_blocked_reason?: string })?.advance_blocked_reason;
      if (msg) {
        setResolveMessage(msg);
      }
    } catch (err) {
      setError(extractApiError(err, 'Failed to resolve flag'));
    }
  };

  return (
    <div
      className={cn(
        'rounded-md border p-3',
        resolved && 'border-zinc-100 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-800/50',
        !resolved && flag.severity === FlagSeverity.BLOCKER && 'border-red-200 bg-red-50 dark:border-red-800/50 dark:bg-red-900/10',
        !resolved && flag.severity !== FlagSeverity.BLOCKER && 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900',
      )}
    >
      <div className="flex items-start gap-2">
        <div className="mt-0.5">{severityIcon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Pill variant={getFlagSeverityVariant(flag.severity)} size="sm">
              {flag.severity}
            </Pill>
            <span className="text-xs text-zinc-500 dark:text-zinc-400">{flag.flag_type}</span>
            {resolved && (
              <span className="flex items-center gap-0.5 text-xs text-emerald-600 dark:text-emerald-400">
                <Check className="h-3 w-3" /> Resolved
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">{flag.message}</p>
          {flag.source && (
            <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">Source: {flag.source}</p>
          )}
          {flag.resolved_reason && (
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              Resolution: {flag.resolved_reason}
            </p>
          )}
        </div>

        {!resolved && !isResolving && (
          <button
            type="button"
            onClick={() => setIsResolving(true)}
            className="shrink-0 rounded-md border border-zinc-200 px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
          >
            Resolve
          </button>
        )}
      </div>

      {/* Post-resolve message from backend */}
      {resolveMessage && (
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-900/20">
          <p className="text-xs font-medium text-amber-800 dark:text-amber-200">{resolveMessage}</p>
        </div>
      )}

      {/* Inline resolve form */}
      {isResolving && (
        <div className="mt-3 space-y-2 border-t border-zinc-100 pt-3 dark:border-zinc-800">
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError(null);
            }}
            placeholder="Reason for resolving this flag..."
            rows={2}
            className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700 placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleResolve}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => {
                setIsResolving(false);
                setReason('');
                setError(null);
              }}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
